/**
 * API 类型适配器
 *
 * 处理前端类型与后端 API 类型之间的转换
 * 解决数据模型差异问题
 */

import type {
  TodoServiceUpdateTodoData,
  TodoV1Comment,
  TodoV1CreateTodoRequest,
  TodoV1Todo,
  TodoV1TodoActivity,
} from "@zero/api";
import { Priority } from "@zero/types/enum";
import type {
  ActivityEventType,
  ActivityItem,
  CommentItem,
  CreateTodoParams,
  TodoItem,
  TodoStatus,
  UpdateTodoParams,
} from "@zero/types/todo";
import { parseApiDate, parseOptionalApiDate, toApiTimestamp } from "../utils/date";
import { canDeleteComment, canEditComment, deriveTodoRelationship } from "../utils/todoRights";

export type ApiTodoPriority = NonNullable<TodoV1Todo["priority"]>;
export type ApiTodoState = NonNullable<TodoV1Todo["state"]>;
export type ApiTodoUpdateBody = TodoServiceUpdateTodoData["body"];

export const TODO_STATUS = {
  pending: "pending",
  inProgress: "inProgress",
  completed: "completed",
  archived: "archived",
} as const satisfies Record<string, TodoStatus>;

export const API_TODO_STATUS = {
  pending: "PENDING",
  inProgress: "IN_PROGRESS",
  completed: "COMPLETED",
  archived: "ARCHIVED",
} as const satisfies Record<string, ApiTodoState>;

// 前端优先级 → 后端优先级
export function toApiPriority(priority: Priority): ApiTodoPriority {
  const map: Record<Priority, ApiTodoPriority> = {
    [Priority.Low]: "TODO_PRIORITY_LOW",
    [Priority.Medium]: "TODO_PRIORITY_MEDIUM",
    [Priority.High]: "TODO_PRIORITY_HIGH",
    [Priority.Urgent]: "TODO_PRIORITY_URGENT",
  };
  return map[priority] ?? "TODO_PRIORITY_MEDIUM";
}

// 后端优先级 → 前端优先级
export function fromApiPriority(priority: ApiTodoPriority | undefined): Priority {
  const map: Record<ApiTodoPriority, Priority> = {
    TODO_PRIORITY_LOW: Priority.Low,
    TODO_PRIORITY_MEDIUM: Priority.Medium,
    TODO_PRIORITY_HIGH: Priority.High,
    TODO_PRIORITY_URGENT: Priority.Urgent,
  };
  return priority ? map[priority] : Priority.Medium;
}

// 前端状态 → 后端状态
export function toApiStatus(status: UpdateTodoParams["status"]): ApiTodoState | undefined {
  if (status === "inProgress") return API_TODO_STATUS.inProgress;
  if (status === "completed") return API_TODO_STATUS.completed;
  if (status === "archived") return API_TODO_STATUS.archived;
  return undefined;
}

// 后端状态 → 前端状态
export function fromApiStatus(status: ApiTodoState): TodoStatus {
  if (status === API_TODO_STATUS.inProgress) return TODO_STATUS.inProgress;
  if (status === API_TODO_STATUS.completed) return TODO_STATUS.completed;
  if (status === API_TODO_STATUS.archived) return TODO_STATUS.archived;
  return TODO_STATUS.pending;
}

function fromApiActivityEventType(eventType: TodoV1TodoActivity["eventType"]): ActivityEventType {
  const map: Record<NonNullable<TodoV1TodoActivity["eventType"]>, ActivityEventType> = {
    ACTIVITY_EVENT_TYPE_CREATED: "created",
    ACTIVITY_EVENT_TYPE_UPDATED: "updated",
    ACTIVITY_EVENT_TYPE_STATUS_CHANGED: "statusChanged",
    ACTIVITY_EVENT_TYPE_ASSIGNED: "assigned",
    ACTIVITY_EVENT_TYPE_UNASSIGNED: "unassigned",
    ACTIVITY_EVENT_TYPE_COMMENT_ADDED: "commentAdded",
    ACTIVITY_EVENT_TYPE_COMMENT_DELETED: "commentDeleted",
    ACTIVITY_EVENT_TYPE_DELETED: "deleted",
  };
  return eventType ? map[eventType] : "updated";
}

function toApiTags(category?: string, tags?: string[]): string[] | undefined {
  const values = [category, ...(tags ?? [])].map((tag) => tag?.trim()).filter((tag): tag is string => Boolean(tag));
  const uniqueValues = Array.from(new Set(values));
  return uniqueValues.length > 0 ? uniqueValues : undefined;
}

// 后端 Todo → 前端 TodoItem
export function fromApiTodo(apiTodo: TodoV1Todo, currentUserId?: string): TodoItem {
  const ownerId = apiTodo.userId ?? "";
  const assigneeId = apiTodo.assigneeId || undefined;
  const status = fromApiStatus(apiTodo.state ?? API_TODO_STATUS.pending);
  const apiTags = apiTodo.tags ?? [];
  return {
    id: apiTodo.id || crypto.randomUUID(),
    title: apiTodo.title || "",
    description: apiTodo.description,
    status,
    priority: fromApiPriority(apiTodo.priority),
    dueDate: parseOptionalApiDate(apiTodo.dueTime),
    completedAt: parseOptionalApiDate(apiTodo.completedTime),
    category: apiTags[0] || "其他",
    tags: apiTags.slice(1),
    ownerId,
    realmId: apiTodo.realmId ?? "",
    assigneeId,
    relationship: deriveTodoRelationship({ ownerId, assigneeId }, currentUserId),
    createdAt: parseApiDate(apiTodo.createTime) ?? new Date(),
    updatedAt: parseApiDate(apiTodo.updateTime) ?? new Date(),
  };
}

export function fromApiComment(apiComment: TodoV1Comment, todo: TodoItem, currentUserId?: string): CommentItem {
  const comment = {
    id: apiComment.id || crypto.randomUUID(),
    todoId: apiComment.todoId || todo.id,
    authorId: apiComment.authorId ?? "",
    content: apiComment.content ?? "",
    createdAt: parseApiDate(apiComment.createTime) ?? new Date(),
    updatedAt: parseApiDate(apiComment.updateTime) ?? new Date(),
  };
  return {
    ...comment,
    canEdit: canEditComment(comment, currentUserId),
    canDelete: canDeleteComment(comment, todo, currentUserId),
  };
}

export function fromApiActivity(apiActivity: TodoV1TodoActivity): ActivityItem {
  return {
    id: apiActivity.id || crypto.randomUUID(),
    todoId: apiActivity.todoId ?? "",
    actorId: apiActivity.actorId ?? "",
    eventType: fromApiActivityEventType(apiActivity.eventType),
    summary: apiActivity.summary ?? "",
    metadata: apiActivity.metadata ?? {},
    createdAt: parseApiDate(apiActivity.createTime) ?? new Date(),
  };
}

// 前端创建参数 → 后端创建请求
export function toApiCreateTodo(params: CreateTodoParams, _userId: string): TodoV1CreateTodoRequest {
  return {
    title: params.title,
    description: params.description,
    dueTime: toApiTimestamp(params.dueDate),
    priority: toApiPriority(params.priority),
    tags: toApiTags(params.category, params.tags),
  };
}

// 前端更新参数 → 后端更新请求
export function toApiUpdateTodo(params: UpdateTodoParams): ApiTodoUpdateBody {
  const request: ApiTodoUpdateBody = {};

  if (params.title !== undefined) request.title = params.title;
  if (params.description !== undefined) request.description = params.description;
  if (params.dueDate !== undefined) request.dueTime = toApiTimestamp(params.dueDate);
  if (params.priority !== undefined) request.priority = toApiPriority(params.priority);
  if (params.category !== undefined || params.tags !== undefined)
    request.tags = toApiTags(params.category, params.tags);

  const status = toApiStatus(params.status);
  if (status) {
    request.state = status;
  }

  return request;
}

// 导出类型
export type { TodoItem, CreateTodoParams, UpdateTodoParams };
